/**
 * RiderWorkStatusPage Component
 * 라이더 근무 현황 페이지
 */
import React, { useEffect, useState } from 'react';
import Loading from '@/shared/components/feedback/Loading';
import { useRiderWorkStatus } from '../hooks/useRiderWorkStatus';
import { riderWorkStatusApi } from '../api/RiderWorkStatusApi';
import FilterSection from './FilterSection';
import WorkStatusTable from './WorkStatusTable';
import { Supplier, SupplierListApiResponse, SupplierApiItem } from '../types/riderWorkStatus.types';
import '@/features/rider/riderStatus/styles/RiderStatusPage.css';
import '../styles/RiderWorkStatusPage.css';

const RiderWorkStatusPage: React.FC = () => {
    const {
        loading,
        excelDownloading,
        workStatusData,
        filters,
        isSupplierUser,
        handleFilterChange,
        handleSearch,
        handleExcelDownload
    } = useRiderWorkStatus();

    const [suppliers, setSuppliers] = useState<Supplier[]>([]);
    const [suppliersLoading, setSuppliersLoading] = useState(false);

    /**
     * 협력사 목록 조회 (본사 사용자만)
     */
    useEffect(() => {
        if (isSupplierUser) return;

        const fetchSuppliers = async () => {
            try {
                setSuppliersLoading(true);
                const response = await riderWorkStatusApi.getSuppliers();
                let suppliersList: Supplier[] = [];

                if (Array.isArray(response)) {
                    suppliersList = response.map((item: SupplierApiItem) => ({
                        centerId: item.centerId || item.suppId || item.id || '',
                        name: item.name || item.suppName || item.centerName || ''
                    }));
                } else if (response && typeof response === 'object') {
                    const responseObj = response as SupplierListApiResponse;
                    if ('suppliers' in responseObj && Array.isArray(responseObj.suppliers)) {
                        suppliersList = responseObj.suppliers.map((item: SupplierApiItem) => ({
                            centerId: item.centerId || item.suppId || item.id || '',
                            name: item.name || item.suppName || item.centerName || ''
                        }));
                    } else if ('data' in responseObj && Array.isArray(responseObj.data)) {
                        suppliersList = responseObj.data.map((item: SupplierApiItem) => ({
                            centerId: item.centerId || item.suppId || item.id || '',
                            name: item.name || item.suppName || item.centerName || ''
                        }));
                    }
                }

                setSuppliers(suppliersList);
            } catch {
                setSuppliers([]);
            } finally {
                setSuppliersLoading(false);
            }
        };

        fetchSuppliers();
    }, [isSupplierUser]);

    if (loading && workStatusData.length === 0) {
        return (
            <div className="rider-status-content">
                <div className="loading-container">
                    <Loading message="라이더 근무 현황을 불러오는 중..." />
                </div>
            </div>
        );
    }

    return (
        <div className="rider-status-content">
            {/* 페이지 헤더 */}
            <div className="page-header">
                <h1 className="page-title">라이더 근무 현황</h1>
            </div>

            {/* 검색 필터 영역 */}
            <FilterSection
                filters={filters}
                suppliers={suppliers}
                suppliersLoading={suppliersLoading}
                isSupplierUser={isSupplierUser}
                loading={loading}
                excelDownloading={excelDownloading}
                onFilterChange={handleFilterChange}
                onSearch={handleSearch}
                onExcelDownload={handleExcelDownload}
            />

            {/* 근무 현황 테이블 영역 */}
            <WorkStatusTable
                data={workStatusData}
                loading={loading}
                minWorkDays={filters.minWorkDays ? parseInt(filters.minWorkDays, 10) : undefined}
            />
        </div>
    );
};

export default RiderWorkStatusPage;

